import {
  GroupedAmenities,
  PrivateOfficeSubType,
} from "@/features/private-room/list/adapters/get-privateroom/get-privateroom.types";
import { ScoreDetail } from "@/features/vision-360/types";
import { WobaInventoryRoomResponse } from "@/features/private-room/photos/adapters/get-room-photos/get-room-photos.types";

type Attribute = {
  id?: number;
  content?: string;
  name: string;
  contentType: string;
};

type Amenity = {
  id?: number;
  status: string;
  name: string;
  attributes?: Attribute[];
};

type PrivateOfficeBaseParams = {
  label: string;
  spaceId: number;
  note?: string;
  capacity: number;
  size: number;
  yearlyPrice?: number;
  monthlyPrice: number;
  guaranteeFee?: string | number;
  status: number;
  amenities: Amenity[];
  pictures?: string[];
};

type CreatePrivateOfficeParams = PrivateOfficeBaseParams;
type UpdatePrivateOfficeParams = PrivateOfficeBaseParams;

export enum PrivateOfficeApprovalStatus {
  all = "all",
  approved = "approved",
  pending = "pending",
}

type Demand = {
  categoryIds?: number[];
  longitude?: string;
  latitude?: string;
  distance?: number;
  cep?: string;
  minCapacity?: number | null;
  maxCapacity?: number | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  amenitiesSlugs?: string[];
  amenitiesSlugsOptional?: string[];
  unitCreditPrice?: number | null;
  minCapacityWeight?: number | null;
  maxCapacityWeight?: number | null;
  minPriceWeight?: number | null;
  maxPriceWeight?: number | null;
  uf?: string;
  privateOfficeStatus?: number[];
  lifecycleStates?: string[];
  skipRecentUpdatesFilter?: boolean;
  approvalStatus?: PrivateOfficeApprovalStatus;
  includeLegacy?: boolean;
  includeDeleted?: boolean;
  searchType?: string;
  location?: string;
  cityName?: string;
  neighborhood?: string[];
  privateOfficeIds?: string[] | null;
  propertyType?: string[];
  includeUnavailableWithinPeriod?: number;
};

type ResultItem = {
  demand_name: string;
  match_percent: string;
  space_id: number;
  private_room_id: string;
  search_id: number;
  label: string;
  capacity: number;
  monthly_price: number;
  monthly_credit_price: number;
  size: number;
  dpp: string;
  status: number;
  days_since_update: string;
  is_outdated: boolean | null;
  threshold_days: number | null;
  distance_km: number;
  amenities: string | null;
  attributes: null;
  details: {
    id: number;
    category?: number;
    space_id: number;
    label: string;
    size: number;
    capacity: number;
    monthly_price: number;
    yearly_price: null;
    guarantee_fee: null;
    contract_fine: null;
    amenities: string;
    extra_services: string | null;
    shared_amenities: null;
    shared_extra_services: null;
    advantages: string | null;
    picture_url: string | null;
    picture_url1: string | null;
    picture_url2: string | null;
    picture_url3: string | null;
    picture_url4: string | null;
    picture_url5: string | null;
    notes: string | null;
    is_fulltime: null;
    status: number;
    is_virtual: boolean;
    is_pre_available: boolean;
    company_id: null;
    contracted_at: null;
    end_contracted_at: null;
    data_updated_at: string;
    registrant_user_id: null;
    engagement_level: number;
    available_date: string;
    created_at: string;
    updated_at: string;
    deleted_at: null;
    created_by_user_id: null;
    updated_by_user_id: null;
    grouped_amenities?: GroupedAmenities[];
    external_url?: string;
  };
  space_details: {
    id: number;
    acknowledge_date: null;
    name: string;
    enabled: boolean;
    type: number;
    latitude: string;
    longitude: string;
    cityname: string;
    neighborhood: string;
    address: string;
    amenities: string;
    vanish_name: string;
    photos?: string[];
    /** AVCB / alvará do espaço (PED-14095): vigentes e cadastrados. `false` =
     * não informado (ou vencido), nunca "o espaço não tem". */
    has_avcb?: boolean;
    has_permit?: boolean;
    avcb_expiry_date?: string | null;
    permit_expiry_date?: string | null;
  };
  detailed_score_analysis: {
    criterion: string;
    requested: string;
    offered: string;
    matched: boolean;
  }[];
  score_details?: ScoreDetail;
  source?: string;
  external_url?: string;
  origin?: string;
  sub_type?: PrivateOfficeSubType | null;
  lifecycleState?: string | null;
  availabilityDate?: string | null;
};

interface SearchPrivateOffice {
  id: string;
  demands: number;
  spaces_returned: number;
  spaces_returned_ids: number[];
  private_rooms_returned_ids: number[];
  demands_without_results: number;
  total_results: number;
  results: ResultItem[];
}

interface SearchPrivateOfficesParams {
  demands?: Record<string, Demand>;
  term?: string;
  dealUrl?: string;
  spaceTypes?: number[];
}

export interface SearchPrivateOfficeInventoryParams {
  spaces: string[];
  skipRecentUpdatesFilter?: boolean;
}

export interface SearchTolerance {
  capacity?: number;
  price?: number;
  distance?: number;
}

export interface SearchFilters {
  minCapacity?: number | null;
  maxCapacity?: number | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  amenities?: string[];
  amenitiesOptional?: string[];
  status?: number[];
  approvalStatus?: string;
  skipRecentUpdatesFilter?: boolean;
  unitCreditPrice?: number;
  includeLegacy?: boolean;
  includeDeleted?: boolean;
  includeIntegration?: boolean;
  includeRoomCombinations?: boolean;
  /** Só salas cujo espaço tem AVCB vigente (PED-14095) */
  hasAvcb?: boolean;
  /** Só salas cujo espaço tem alvará de funcionamento vigente (PED-14095) */
  hasPermit?: boolean;
}

export interface SearchParams {
  mode: "location" | "neighborhood" | "space" | "room";
  location?: { latitude: number; longitude: number; radius: number };
  neighborhoods?: string[];
  cityName?: string;
  spaceId?: number;
  roomIds?: string[];
  filters?: SearchFilters;
  tolerance?: SearchTolerance;
}

// Private Office Update Request types
export type PrivateOfficeUpdateStatus = "pending" | "approved" | "rejected";

export type PrivateOfficeUpdateField = Record<string, any>;

export interface OfficeInfo {
  id: number;
  name: string;
  picture_url?: string | null;
  picture_url_1?: string | null;
  picture_url_2?: string | null;
  picture_url_3?: string | null;
  picture_url_4?: string | null;
  picture_url_5?: string | null;
}

export interface SpaceInfo {
  name: string;
  cityname: string;
}

export interface RequestedBy {
  id: number;
  username: string;
}

export interface ReviewedBy {
  id: number;
  name: string;
  email: string;
}

export interface AmenityChange {
  slug: string;
  name: string;
  changeType: "status" | "attributes" | "both" | "added" | "removed";
  statusChange?: {
    from: string;
    to: string;
  };
  attributesChange?: {
    added: number;
    removed: number;
    modified: number;
  };
}

export interface AmenitiesSummary {
  total_changed: number;
  added_count: number;
  removed_count: number;
  modified_count: number;
  changed_slugs: string[];
  changes: AmenityChange[];
  summary: string;
}

export interface AdditionalData {
  amenities_summary?: AmenitiesSummary;
  operating_hours_summary?: string;
  originalStatus?: {
    id: number;
    reason?: string;
    statusDate?: string;
    available_date?: string;
  };
  [key: string]: any;
}

export interface InventoryOffice extends WobaInventoryRoomResponse {}

export interface PrivateOfficeUpdateRequest {
  _id: string;
  status: PrivateOfficeUpdateStatus;
  fields_to_update: PrivateOfficeUpdateField;
  previous_values: PrivateOfficeUpdateField;
  office_info: OfficeInfo;
  space_info: SpaceInfo;
  requested_by: RequestedBy;
  reviewed_by?: ReviewedBy;
  reviewed_at?: string; // ISO string (optional)
  created_at: string; // ISO string
  updated_at: string; // ISO string
  rejection_reason?: string;
  additional_data?: AdditionalData;
  current_inventory_office?: InventoryOffice;
}

export interface GroupedUpdateRequest {
  _id: number;
  requests: PrivateOfficeUpdateRequest[];
  count: number;
  lastUpdate: string;
}

export interface Pagination {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
}

export interface Meta {
  total_groups: number;
  grouped_by: string;
}

export interface GetPrivateOfficeUpdateRequestsResponse {
  success: boolean;
  message: string;
  data: GroupedUpdateRequest[];
  meta: Meta;
  pagination: Pagination;
}

// Image selection types
export type PictureProperty =
  | "picture_url"
  | "picture_url_1"
  | "picture_url_2"
  | "picture_url_3"
  | "picture_url_4"
  | "picture_url_5";

export interface CurrentRoomImages {
  picture_url: string | null;
  picture_url_1: string | null;
  picture_url_2: string | null;
  picture_url_3: string | null;
  picture_url_4: string | null;
  picture_url_5: string | null;
}

export interface RoomImage {
  property: PictureProperty;
  url: string | null;
  name: string;
  isEmpty: boolean;
}

export interface RoomImagesResponse {
  success: boolean;
  data: {
    room_id: number;
  } & CurrentRoomImages;
}

export interface ImageApprovalData {
  requestId: string;
  replace_property: PictureProperty;
  new_image_url: string;
}

export interface OptimizeImageRequest {
  imageUrl: string;
}

export interface OptimizeImageResponse {
  enhancedUrl: string;
}

export type {
  Demand,
  ResultItem,
  SearchPrivateOffice,
  SearchPrivateOfficesParams,
  CreatePrivateOfficeParams,
  UpdatePrivateOfficeParams,
};

/** GET /private-offices/spaces/:spaceId/compliance-documents (PED-14095) */
export interface SpaceComplianceDocument {
  /** Present and not expired — same rule as the search filter and the badge */
  valid: boolean;
  url: string | null;
  expiry_date: string | null;
}

export interface SpaceComplianceDocuments {
  space_id: number;
  avcb: SpaceComplianceDocument;
  permit: SpaceComplianceDocument;
}
